import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function AboutSection() {
  const { ref: sectionRef, isVisible } = useScrollReveal<HTMLElement>();

  return (
    <section id="about" ref={sectionRef} className="bg-axelia-bg py-20 md:py-28 lg:py-32">
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-20 max-w-6xl mx-auto">
          {/* Left - 35% */}
          <div className={`w-full lg:w-[35%] shrink-0 transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
            <span className="inline-block px-4 py-1.5 border border-axelia-text/20 rounded-full text-xs font-medium text-axelia-text-secondary tracking-widest mb-8">
              ABOUT US
            </span>
            <div className="relative aspect-[3/4] rounded-[2rem] overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Sophisticated%20modern%20office%20interior%20with%20warm%20natural%20lighting%2C%20minimalist%20Japanese%20corporate%20workspace%20with%20wooden%20accents%20and%20plants%2C%20elegant%20architectural%20detail%20with%20soft%20shadows%2C%20cream%20and%20warm%20gray%20tones%2C%20intellectual%20atmosphere%20suitable%20for%20about%20section%20of%20technology%20consulting%20company&width=600&height=800&seq=axelia-about-01&orientation=portrait"
                alt="AXELIA株式会社 オフィス"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Right - 65% */}
          <div className={`w-full lg:w-[65%] flex flex-col transition-all duration-700 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-[2.2rem] font-bold text-axelia-text leading-[1.35] mb-6">
              人と組織の成長を
              <br />
              実務に根ざした支援で
              <br />
              未来を創造する
            </h2>

            <div className="space-y-2 mb-8">
              <p className="text-sm md:text-base text-axelia-text-secondary leading-[1.9]">
                AXELIA株式会社は、人と組織の可能性を引き出し、
                実務に根ざした支援と創造的な発想で社会に新たな価値を提供することを使命としています。
              </p>
              <p className="text-sm md:text-base text-axelia-text-secondary leading-[1.9]">
                人材育成コンサルティングを起点に、システムエンジニアリング、
                Web制作まで一貫したサービスを展開し、お客様の課題解決に取り組んでいます。
              </p>
            </div>

            {/* Company Info */}
            <div className="border-t border-axelia-bg-warm pt-8">
              <h3 className="font-serif text-lg font-bold text-axelia-text mb-6">会社情報</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className={`transition-all duration-700 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                  <p className="text-xs text-axelia-text-muted uppercase tracking-wider mb-1.5">
                    会社名
                  </p>
                  <p className="text-sm font-medium text-axelia-text">AXELIA株式会社</p>
                </div>
                <div className={`transition-all duration-700 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                  <p className="text-xs text-axelia-text-muted uppercase tracking-wider mb-1.5">
                    代表者
                  </p>
                  <p className="text-sm font-medium text-axelia-text">松葉 崇紘</p>
                </div>
                <div className={`sm:col-span-2 transition-all duration-700 delay-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                  <p className="text-xs text-axelia-text-muted uppercase tracking-wider mb-1.5">
                    所在地
                  </p>
                  <p className="text-sm font-medium text-axelia-text">
                    〒104-0041 東京都中央区八丁堀3丁目15番6-509号
                  </p>
                </div>
                <div className={`transition-all duration-700 delay-[550ms] ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                  <p className="text-xs text-axelia-text-muted uppercase tracking-wider mb-1.5">
                    メール
                  </p>
                  <p className="text-sm font-medium text-axelia-text break-all">
                    customer@axelia-tech.com
                  </p>
                </div>
                <div className={`transition-all duration-700 delay-[575ms] ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                  <p className="text-xs text-axelia-text-muted uppercase tracking-wider mb-1.5">
                    電話番号
                  </p>
                  <p className="text-sm font-medium text-axelia-text">
                    03-6821-7456
                  </p>
                </div>
                <div className={`sm:col-span-2 transition-all duration-700 delay-600 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                  <p className="text-xs text-axelia-text-muted uppercase tracking-wider mb-1.5">
                    業務内容
                  </p>
                  <ul className="text-sm text-axelia-text space-y-1.5">
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-axelia-accent mt-2 shrink-0" />
                      人材育成に関するコンサルティング業務
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-axelia-accent mt-2 shrink-0" />
                      システムエンジニアリングサービス
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-axelia-accent mt-2 shrink-0" />
                      インターネットを利用したウェブサイトの企画、構築及びコンテンツ制作業務
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <a
              href="#contact"
              className="mt-10 inline-flex items-center gap-2 px-8 py-4 bg-axelia-text text-white text-sm font-medium rounded-full hover:bg-axelia-accent transition-colors duration-300 self-start whitespace-nowrap"
            >
              お問い合わせはこちら
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
